import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/admin/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/admin/ui/table';
import { Input } from '@/components/admin/ui/input';
import { Search, Gift, Download } from 'lucide-react';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/admin/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/admin/ui/select';

interface SweepstakesEntry {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  wedding_date: string;
  wedding_location: string;
  priority_choice: 'affordable_pricing' | 'great_reviews' | 'beautiful_portfolio';
  marketing_consent: boolean;
  created_account: boolean;
  user_id: string | null;
  entry_status: 'pending' | 'winner' | 'contacted' | 'converted' | 'disqualified';
  created_at: string;
  updated_at: string;
}

const priorityLabels = {
  affordable_pricing: 'Affordable Pricing',
  great_reviews: 'Great Reviews',
  beautiful_portfolio: 'Beautiful Portfolio',
};

const statusLabels = {
  pending: 'Pending',
  winner: 'Winner',
  contacted: 'Contacted',
  converted: 'Converted',
  disqualified: 'Disqualified',
};

const SweepstakesEntries = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [entries, setEntries] = useState<SweepstakesEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const { toast } = useToast();

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('sweepstakes_entries')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error('Error fetching sweepstakes entries:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sweepstakes entries',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateEntryStatus = async (entryId: string, newStatus: string) => {
    try {
      const { error } = await supabaseAdmin
        .from('sweepstakes_entries')
        .update({ entry_status: newStatus })
        .eq('id', entryId);

      if (error) throw error;

      // Update local state
      setEntries(entries.map(entry =>
        entry.id === entryId ? { ...entry, entry_status: newStatus as any } : entry
      ));

      toast({
        title: 'Success',
        description: 'Entry status updated',
      });
    } catch (error) {
      console.error('Error updating entry status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update entry status',
        variant: 'destructive',
      });
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Name',
      'Email',
      'Phone',
      'Wedding Date',
      'Location',
      'Priority',
      'Status',
      'Created At',
    ];

    const rows = filteredEntries.map(entry => [
      entry.full_name,
      entry.email,
      entry.phone || '',
      entry.wedding_date,
      entry.wedding_location,
      priorityLabels[entry.priority_choice],
      statusLabels[entry.entry_status],
      new Date(entry.created_at).toLocaleDateString(),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `sweepstakes-entries-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: 'Success',
      description: 'Entries exported to CSV',
    });
  };

  // Filtered and searched entries
  const filteredEntries = useMemo(() => {
    let filtered = entries;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(entry => entry.entry_status === statusFilter);
    }

    // Apply search query
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.full_name.toLowerCase().includes(searchLower) ||
        entry.email.toLowerCase().includes(searchLower) ||
        entry.wedding_location.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [entries, searchQuery, statusFilter]);

  const stats = useMemo(() => ({
    total: entries.length,
    pending: entries.filter(e => e.entry_status === 'pending').length,
    winners: entries.filter(e => e.entry_status === 'winner').length,
    contacted: entries.filter(e => e.entry_status === 'contacted').length,
    converted: entries.filter(e => e.entry_status === 'converted').length,
  }), [entries]);

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Gift className="h-8 w-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-foreground">Leads</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            View and manage lead generation entries from homepage sweepstakes
          </p>
        </div>
        <Button onClick={exportToCSV} variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Total Entries</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.pending}</div>
            <p className="text-xs text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.winners}</div>
            <p className="text-xs text-muted-foreground">Winners</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.contacted}</div>
            <p className="text-xs text-muted-foreground">Contacted</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.converted}</div>
            <p className="text-xs text-muted-foreground">Converted</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Entries</CardTitle>
          <CardDescription>Manage sweepstakes entries and track conversion</CardDescription>
          <div className="flex gap-4 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="winner">Winner</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="disqualified">Disqualified</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading entries...</div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery || statusFilter !== 'all' ? 'No entries match your filters' : 'No entries yet'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Wedding Date</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date Entered</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-medium">{entry.full_name}</TableCell>
                      <TableCell>{entry.email}</TableCell>
                      <TableCell className="text-muted-foreground">{entry.phone || 'N/A'}</TableCell>
                      <TableCell>{entry.wedding_date}</TableCell>
                      <TableCell>{entry.wedding_location}</TableCell>
                      <TableCell>
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                          {priorityLabels[entry.priority_choice]}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            entry.entry_status === 'winner'
                              ? 'bg-yellow-100 text-yellow-700'
                              : entry.entry_status === 'converted'
                              ? 'bg-green-100 text-green-700'
                              : entry.entry_status === 'contacted'
                              ? 'bg-blue-100 text-blue-700'
                              : entry.entry_status === 'disqualified'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {statusLabels[entry.entry_status]}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(entry.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={entry.entry_status}
                          onValueChange={(value) => updateEntryStatus(entry.id, value)}
                        >
                          <SelectTrigger className="w-[130px] h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="winner">Winner</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="converted">Converted</SelectItem>
                            <SelectItem value="disqualified">Disqualified</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SweepstakesEntries;
